import { Directive, DirectiveBinding } from 'vue'

interface DragBinding {
  x?: number
  y?: number
  minX?: number
  maxX?: number
  minY?: number
  maxY?: number
  onStart?: () => void
  onEnd?: (x: number, y: number) => void
  onMove?: (x: number, y: number) => void
  open: () => void
}

// 扩展 HTMLElement 类型存储处理器
declare global {
  interface HTMLElement {
    _dragHandlers?: {
      onMouseDown: (e: MouseEvent) => void
      onMouseMove: (e: MouseEvent) => void
      onMouseUp: (e: MouseEvent) => void
    }
  }
}

const vDrag: Directive = {
  mounted (el: HTMLElement, binding: DirectiveBinding<DragBinding>) {
    const { x = 0, y = 0, minX = -Infinity, maxX = Infinity, minY = -Infinity, maxY = Infinity, onStart, onEnd, onMove, open } = binding.value || {}

    let currentX = x
    let currentY = y
    let startX = 0
    let startY = 0
    let isDragging = false

    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      isDragging = true
      startX = e.clientX - currentX
      startY = e.clientY - currentY

      // 应用拖拽样式
      el.style.transition = 'none'
      // 触发开始回调
      onStart?.()

      // 在 document 上绑定事件
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
      el.addEventListener('click', open)
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return

      // 计算新位置并限制边界
      currentX = Math.min(maxX, Math.max(minX, e.clientX - startX))
      currentY = Math.min(maxY, Math.max(minY, e.clientY - startY))

      // 应用变换
      el.style.transform = `translate(0px, ${currentY}px)`

      // 触发移动回调
      onMove?.(currentX, currentY)
      el.removeEventListener('click', open)
    }

    const onMouseUp = (e: MouseEvent) => {
      if (!isDragging) return

      isDragging = false

      // 恢复样式
      el.style.transition = 'all 0.5s'

      // 触发结束回调
      onEnd?.(currentX, currentY)

      // 移除 document 上的事件监听
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      setTimeout(() => {
        el.addEventListener('click', open)
      }, 500)
    }

    // 初始位置
    el.style.transform = `translate(0px, ${y}px)`

    // 监听元素上的 mousedown
    el.addEventListener('mousedown', onMouseDown)

    // 保存处理器以便卸载
    el._dragHandlers = { onMouseDown, onMouseMove, onMouseUp }
  },

  unmounted (el: HTMLElement) {
    if (el._dragHandlers) {
      const { onMouseDown, onMouseMove, onMouseUp } = el._dragHandlers

      // 移除元素上的监听
      el.removeEventListener('mousedown', onMouseDown)

      // 确保移除 document 上的监听
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)

      // 恢复样式
      el.style.cursor = ''
      el.style.userSelect = ''
      el.style.position = ''

      delete el._dragHandlers
    }
  }
}

export default vDrag
